import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import {
  CreditCard, Search, X, IndianRupee, Calendar, FileText, MessageSquare,
  Wifi, Info, Receipt, Mail, Copy, CheckCircle2, Link2, CalendarClock,
  Clock, AlertTriangle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useNavigate, useParams } from "react-router-dom";
import UploadWithS3 from "../Common/UploadWithS3";
import useRazorpay from "../../hooks/useRazorpay";
import {
  AppButton, AppModal, PageHeader, FormActionBar, ResetButton, UserAvatar,
} from "../ui";
import FeePlanModal from "./FeePlanModal";
import "./fees-collection.scss";
import "./payment.scss";
import "./fee-collect2.scss";

const INSTALLMENT_STATUS = {
  paid:     { label: "Paid",     cls: "paid",     Icon: CheckCircle2 },
  partial:  { label: "Partial",  cls: "partial",  Icon: Clock },
  due:      { label: "Due",      cls: "due",      Icon: CalendarClock },
  overdue:  { label: "Overdue",  cls: "overdue",  Icon: AlertTriangle },
  upcoming: { label: "Upcoming", cls: "upcoming", Icon: Clock },
  waived:   { label: "Waived",   cls: "waived",   Icon: CheckCircle2 },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit", timeZone: "Asia/Kolkata" });
}

// Manual payment sub-modes (online modes are handled by the channel toggle).
const PAYMENT_MODES = ["UPI", "Cash", "Bank Transfer", "Card", "Cheque"];

const initialData = {
  currentPayment: "",
  paymentMode: "",
  referenceNumber: "",
  paidTo: "",
  receivedBy: "",
  date: new Date().toISOString().slice(0, 10),
  refImage: null,
  nextPaymentDate: "",
  comments: "",
};

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const FeeCollectionForm = ({ mode = "add" }) => {
  const { feeId } = useParams();
  const isEditMode = mode === "edit";

  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [feeLoading, setFeeLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [isRazorpayTxn, setIsRazorpayTxn] = useState(false);
  const [err, setErr] = useState("");
  const [disabledSave, setDisabledSave] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [rzpProcessing, setRzpProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [lastPaymentInfo, setLastPaymentInfo] = useState(null);
  // ── Payment-link mode ──────────────────────────────────────────────
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [sentLink, setSentLink] = useState(null); // { url, email } once a link is sent
  const [linkCopied, setLinkCopied] = useState(false);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  // ── Installment (EMI) plan for the selected enrollment ──
  const [feePlan, setFeePlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  // ── Payment channel (manual vs online) ──
  const [payChannel, setPayChannel] = useState("manual");
  const [sendingWa, setSendingWa] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const [form, setForm] = useState(initialData);

  // Pristine snapshot for the Reset action (captured once after initial load).
  const pristineRef = useRef(initialData);
  const pristineCaptured = useRef(false);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setStudentResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch payment config to know if LMS payment mode is active
  useEffect(() => {
    apiClient.get("/payments/config")
      .then(({ data }) => setPaymentConfig(data))
      .catch(() => {});
  }, []);

  // ── Edit mode: load existing fee transaction ───────────────────────
  useEffect(() => {
    if (!isEditMode || !feeId) return;
    setEditLoading(true);
    apiClient.get(`/fees/${feeId}`)
      .then(({ data }) => {
        const txn = data.transaction;
        if (!txn) return;

        // Prefill student
        const stu = txn.studentId;
        if (stu) {
          setSelectedStudent(stu);
          // Also load course assignments for this student
          apiClient.get(`/course-assignments/student/${stu._id}`)
            .then(({ data: caData }) => {
              const active = (caData.assignments || []).filter((a) => a.status === "active");
              setCourseAssignments(active);
              if (txn.courseAssignmentId) {
                const matchedCa = active.find(
                  (a) => a._id === (txn.courseAssignmentId?._id || txn.courseAssignmentId)
                );
                if (matchedCa) setSelectedAssignment(matchedCa);
                // If populated object came in the transaction, use it directly
                else if (txn.courseAssignmentId?._id) {
                  setSelectedAssignment(txn.courseAssignmentId);
                }
              }
            })
            .catch(() => {});
        }

        // Prefill form
        const rawDate = txn.date ? new Date(txn.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
        const rawNextDate = txn.nextPaymentDate
          ? new Date(txn.nextPaymentDate).toISOString().slice(0, 10)
          : "";
        setForm({
          currentPayment: String(txn.currentPayment || ""),
          paymentMode   : txn.paymentMode || "",
          referenceNumber: txn.referenceNumber || "",
          paidTo        : txn.paidTo || "",
          receivedBy    : txn.receivedBy || "",
          date          : rawDate,
          refImage      : txn.refImage || null,
          nextPaymentDate: rawNextDate,
          comments      : txn.comments || "",
        });

        setIsRazorpayTxn(txn.paymentGateway === "razorpay");
        if (txn.paymentGateway === "razorpay") setPayChannel("online");
      })
      .catch(() => setErr("Failed to load fee details."))
      .finally(() => setEditLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, feeId]);

  // Snapshot the pristine form once the initial (add / edit-load) state settles.
  useEffect(() => {
    if (!editLoading && !pristineCaptured.current) {
      pristineRef.current = form;
      pristineCaptured.current = true;
    }
  }, [editLoading, form]);

  const onCancel = async () => {
    setStudentSearch("");
    setStudentResults([]);
    setSelectedStudent(null);
    setForm(initialData);
    if (form.refImage) {
      await apiClient.delete("/files/delete", { data: { key: form.refImage } });
    }
    navigate(-1);
  };

  const totalFee = selectedAssignment ? Number(selectedAssignment.totalFees || 0) : Number(selectedStudent?.totalFees || 0);
  const paidFee = selectedAssignment ? Number(selectedAssignment.paidFees || 0) : Number(selectedStudent?.paidFees || 0);
  const pendingFee = selectedAssignment ? Number(selectedAssignment.pendingFees || 0) : Number(selectedStudent?.pendingFees || 0);
  const isSelfPaced = selectedAssignment?.courseId?.courseType === 'self-paced';

  const handleStudentSearch = async (value) => {
    setStudentSearch(value);
    setErr("");
    if (value.length < 2) { setStudentResults([]); return; }
    setLoadingStudent(true);
    try {
      const q = /^\d{10}$/.test(value) ? `/students/search?mobile=${value}` : `/students/search?text=${value}`;
      const { data } = await apiClient.get(q);
      setStudentResults(data.students || []);
    } catch {
      setStudentResults([]);
      setErr("Failed to search students");
    }
    setLoadingStudent(false);
  };

  const handleStudentSelect = async (student) => {
    setFeeLoading(true);
    setStudentSearch("");
    setStudentResults([]);
    setSelectedStudent(null);
    setCourseAssignments([]);
    setSelectedAssignment(null);
    try {
      const { data } = await apiClient.get(`/students/${student._id}`);
      setSelectedStudent(data.student);
      setLinkEmail(data.student?.email || "");
      // Fetch course assignments for this student
      try {
        const { data: caData } = await apiClient.get(`/course-assignments/student/${student._id}`);
        const active = (caData.assignments || []).filter((a) => a.status === 'active');
        setCourseAssignments(active);
        // Auto-select if only one course
        if (active.length === 1) setSelectedAssignment(active[0]);
      } catch {
        setCourseAssignments([]);
      }
    } catch {
      setErr("Failed to fetch student details");
    }
    setFeeLoading(false);
  };

  const clearStudent = () => {
    setSelectedStudent(null);
    setStudentSearch("");
    setCourseAssignments([]);
    setSelectedAssignment(null);
    setForm(initialData);
    setLinkEmail("");
    setSentLink(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => setForm(pristineRef.current);

  // ── Load the installment plan for the selected enrollment ──────────────
  const loadPlan = React.useCallback((caId) => {
    if (!caId) { setFeePlan(null); return; }
    setPlanLoading(true);
    apiClient.get(`/fees/plans/${caId}`)
      .then(({ data }) => setFeePlan(data.plan || null))
      .catch(() => setFeePlan(null))
      .finally(() => setPlanLoading(false));
  }, []);

  useEffect(() => {
    setSelectedInstallment(null);
    loadPlan(selectedAssignment?._id);
  }, [selectedAssignment, loadPlan]);

  // Pick an installment → prefill the amount with its remaining balance and
  // tag the payment so it's recorded against this EMI.
  const handleSelectInstallment = (inst) => {
    if (!inst || inst.status === "paid" || inst.status === "waived") return;
    const balance = Math.max((Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0), 0);
    setSelectedInstallment(inst);
    setForm((prev) => ({ ...prev, currentPayment: String(balance) }));
  };

  const nextDueInstallment = feePlan?.installments?.find(
    (i) => i.status === "overdue" || i.status === "due" || i.status === "partial"
  ) || feePlan?.installments?.find((i) => i.status === "upcoming") || null;

  const currentEMI = selectedInstallment || nextDueInstallment;

  const overdueCount = (feePlan?.installments || []).filter((i) => i.status === "overdue").length;
  const overdueAmount = (feePlan?.installments || [])
    .filter((i) => i.status === "overdue")
    .reduce((s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paidAmount) || 0), 0), 0);
  const pendingInstallments = (feePlan?.installments || []).filter((i) => i.status !== "paid" && i.status !== "waived");
  const upcomingDues = pendingInstallments.reduce((s, i) => s + Math.max((Number(i.amount) || 0) - (Number(i.paidAmount) || 0), 0), 0);

  const due = (pendingFee || 0) - (parseFloat(form.currentPayment) || 0);

  const isOnlineMode = form.paymentMode === "Razorpay (Online)";
  const isPaymentLinkMode = form.paymentMode === "Send Payment Link to Student";
  const onlineEnabled = !!paymentConfig?.enabled;

  // Switch payment channel, keeping form.paymentMode coherent.
  const setChannel = (ch) => {
    if (isRazorpayTxn) return;
    setPayChannel(ch);
    setForm((p) => {
      const online = p.paymentMode === "Razorpay (Online)" || p.paymentMode === "Send Payment Link to Student";
      if (ch === "manual" && online) return { ...p, paymentMode: "" };
      if (ch === "online" && !online) return { ...p, paymentMode: "Razorpay (Online)" };
      return p;
    });
  };

  /** Generate a payment link and email it to the student. */
  const handleCreatePaymentLink = async () => {
    if (!selectedStudent) return;
    setLinkSubmitting(true);
    setErr("");
    try {
      const { data } = await apiClient.post("/fee-payment-links", {
        studentId: selectedStudent._id,
        courseAssignmentId: selectedAssignment?._id || undefined,
        amount: parseFloat(form.currentPayment),
        email: linkEmail.trim(),
        description: form.comments || undefined,
        nextPaymentDate: form.nextPaymentDate || undefined,
      });
      setShowPreview(false);
      setSentLink({ url: data.url, email: data.link?.sentToEmail || linkEmail.trim() });
      setLinkCopied(false);
      setToast({ show: true, message: data.message || "Payment link sent.", type: "success" });
    } catch (ex) {
      setShowPreview(false);
      setErr(ex?.response?.data?.message || "Failed to create payment link.");
    }
    setLinkSubmitting(false);
  };

  /** Handle Razorpay checkout flow. */
  const handleRazorpayPayment = async () => {
    if (!selectedStudent) return;
    const amount = parseFloat(form.currentPayment);
    if (!amount || amount <= 0) {
      setErr("Enter a valid payment amount.");
      return;
    }
    setRzpProcessing(true);
    setErr("");
    try {
      const { data: orderData } = await apiClient.post("/payments/create-order", {
        studentId: selectedStudent._id,
        amount,
        description: form.comments || "Fee Payment",
        courseAssignmentId: selectedAssignment?._id || undefined,
      });

      openCheckout(
        orderData,
        async (response) => {
          try {
            const { data: verifyData } = await apiClient.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: orderData.transactionId,
            });
            const txn = verifyData.transaction;
            if (txn?.paymentType === "lms_payment" && txn.commissionAmount > 0) {
              setLastPaymentInfo({
                amount: txn.amount,
                commission: txn.commissionAmount,
                gatewayFee: txn.gatewayFee,
                netPayable: txn.netPayable,
              });
            }
            setToast({ show: true, message: "Online payment collected successfully!", type: "success" });
            // Navigate to the fee details page for the newly created StudentFee.
            const sid = selectedStudent._id;
            const feeRef = txn?.studentFeeId;
            if (sid && feeRef) {
              setTimeout(() => navigate(`/students/${sid}/fees/view/${feeRef}`), 800);
            } else {
              setForm(initialData);
              setSelectedStudent(null);
            }
          } catch {
            setToast({ show: true, message: "Payment received but verification failed. Contact support.", type: "error" });
          }
          setRzpProcessing(false);
        },
        (error) => {
          setToast({ show: true, message: error?.reason || "Payment was cancelled or failed.", type: "error" });
          setRzpProcessing(false);
        },
      );
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to create payment order.";
      setToast({ show: true, message: msg, type: "error" });
      setRzpProcessing(false);
    }
  };

  // Quick action: jump to the "send payment link" flow, prefilling the amount.
  const handleQuickLink = () => {
    setChannel("online");
    setForm((p) => ({
      ...p,
      paymentMode: "Send Payment Link to Student",
      currentPayment:
        p.currentPayment ||
        (currentEMI ? String(Math.max((currentEMI.amount || 0) - (currentEMI.paidAmount || 0), 0)) : (pendingFee ? String(pendingFee) : "")),
    }));
  };

  // Quick action: send an EMI reminder (email + WhatsApp) for the next due EMI.
  const handleSendWhatsApp = async () => {
    if (!feePlan || !nextDueInstallment) return;
    setSendingWa(true);
    try {
      await apiClient.post(`/fees/plans/${feePlan._id}/installments/${nextDueInstallment.seq}/remind`);
      setToast({ show: true, message: "Reminder sent via WhatsApp & email.", type: "success" });
    } catch (ex) {
      setToast({ show: true, message: ex?.response?.data?.message || "Failed to send reminder.", type: "error" });
    }
    setSendingWa(false);
  };

  // Validate the form before showing the preview / submitting.
  const validateBeforeSubmit = () => {
    if (!selectedStudent) return "Select a valid student before collecting fee.";
    const amt = parseFloat(form.currentPayment);
    if (!amt || amt <= 0) return "Enter a valid payment amount.";
    if (!isOnlineMode && !isPaymentLinkMode && !form.paymentMode) return "Select a payment mode.";
    if (isPaymentLinkMode) {
      const e = linkEmail.trim();
      if (!e) return "Enter an email address to send the payment link to.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return "Enter a valid email address.";
    }
    return "";
  };

  // Add mode: open the preview & confirm modal instead of submitting directly.
  const handlePreview = () => {
    setErr("");
    const v = validateBeforeSubmit();
    if (v) { setErr(v); return; }
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isPaymentLinkMode) {
      handleCreatePaymentLink();
      return;
    }
    if (isOnlineMode) {
      // Close the preview first; Razorpay opens its own overlay on this gesture.
      setShowPreview(false);
      handleRazorpayPayment();
      return;
    }
    setDisabledSave(true);
    if (!selectedStudent) {
      setErr("Select a valid student before collecting fee.");
      setDisabledSave(false);
      setShowPreview(false);
      return;
    }
    const payload = {
      ...form,
      studentId: selectedStudent._id,
      courseAssignmentId: selectedAssignment?._id || undefined,
      feePlanId: selectedInstallment ? feePlan?._id : undefined,
      installmentSeq: selectedInstallment?.seq ?? undefined,
    };
    let dataToSend = payload;
    let headers = {};
    if (form.refImage && typeof form.refImage !== "string") {
      dataToSend = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) dataToSend.append(key, value);
      });
      if (form.refImage) dataToSend.append("refImage", form.refImage);
      headers["Content-Type"] = "multipart/form-data";
    }
    try {
      if (isEditMode && feeId) {
        await apiClient.put(`/fees/${feeId}`, dataToSend, { headers });
        setShowPreview(false);
        setToast({ show: true, message: "Fee updated successfully!", type: "success" });
        setTimeout(() => navigate(-1), 1200);
      } else {
        const res = await apiClient.post("/fees/add", dataToSend, { headers });
        if (res.status === 201) {
          const newFee = res.data?.transaction;
          const sid = selectedStudent._id;
          setShowPreview(false);
          setToast({ show: true, message: "Fee collected successfully!", type: "success" });
          // Land on the fee details page for the newly created transaction.
          if (newFee?._id && sid) {
            setTimeout(() => navigate(`/students/${sid}/fees/view/${newFee._id}`), 700);
          } else {
            setForm(initialData);
            setSelectedStudent(null);
          }
        }
      }
    } catch (ex) {
      setShowPreview(false);
      setErr(ex?.response?.data?.message || (isEditMode ? "Failed to update fee." : "Failed to collect fee. Please try again."));
    }
    setDisabledSave(false);
  };

  const amountDisabled = !selectedStudent || isSelfPaced || isRazorpayTxn;
  const planCount = feePlan?.installments?.length || 0;

  return (
    <div className="fee-collect2 has-form-actionbar">
      {editLoading && (
        <div className="fee-form-edit-loading">
          <Spinner animation="border" size="sm" /> Loading fee details...
        </div>
      )}
      <PageHeader
        icon={CreditCard}
        title={isEditMode ? "Edit Fee" : "Collect Fee"}
        description={isEditMode ? "Update the details of this fee transaction" : "Record a new fee payment from a student"}
        backButton
        onBack={() => navigate(-1)}
      />

      <div className="fc2-grid">
        {/* ══════════ MAIN COLUMN ══════════ */}
        <div className="fc2-main">
          {/* Student */}
          <section className="fc2-card">
            <div className="fc2-card__head"><h3 className="fc2-card__title">Student</h3></div>
            {selectedStudent ? (
              <div className="fc2-student">
                <UserAvatar name={selectedStudent.name} size="md" />
                <div className="fc2-student__info">
                  <span className="fc2-student__name">{selectedStudent.name}</span>
                  <span className="fc2-student__meta">
                    {selectedStudent.mobile}{selectedStudent.email ? ` • ${selectedStudent.email}` : ""}
                  </span>
                </div>
                {!isEditMode && (
                  <button className="fc2-student__clear" onClick={clearStudent} title="Change student">
                    <X size={18} />
                  </button>
                )}
              </div>
            ) : (
              <div className="fc2-search" ref={searchRef}>
                <Search size={16} className="fc2-search__icon" />
                <input
                  type="text"
                  className="fc2-search__input"
                  placeholder="Search by name or mobile..."
                  value={studentSearch}
                  onChange={(e) => handleStudentSearch(e.target.value)}
                  autoComplete="off"
                />
                {loadingStudent && <Spinner size="sm" className="fc2-search__spinner" />}
                {studentSearch && studentResults.length > 0 && (
                  <div className="fc2-search__dropdown">
                    {studentResults.map((stu) => (
                      <div key={stu._id} className="fc2-search__item" onClick={() => handleStudentSelect(stu)}>
                        <UserAvatar name={stu.name} size="sm" />
                        <div>
                          <div className="fc2-search__item-name">{stu.name}</div>
                          <div className="fc2-search__item-meta">{stu.mobile}</div>
                        </div>
                        {Number(stu.pendingFees) > 0 && (
                          <span className="fc2-search__item-due">{formatCurrency(stu.pendingFees)} due</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Select Enrollment (Course) */}
          {selectedStudent && courseAssignments.length > 0 && (
            <section className="fc2-card">
              <div className="fc2-card__head"><h3 className="fc2-card__title">Select Enrollment (Course)</h3></div>
              <div className="fc2-enroll">
                {courseAssignments.map((ca) => {
                  const isSel = selectedAssignment?._id === ca._id;
                  const p = Number(ca.pendingFees || 0), pd = Number(ca.paidFees || 0), t = Number(ca.totalFees || 0);
                  const badge = p <= 0 ? { label: "Paid", cls: "paid" } : pd > 0 ? { label: "Partial", cls: "partial" } : { label: "Pending", cls: "pending" };
                  return (
                    <button
                      key={ca._id}
                      type="button"
                      className={`fc2-enroll-card${isSel ? " is-selected" : ""}`}
                      onClick={() => { setSelectedAssignment(ca); setSelectedInstallment(null); setForm((prev) => ({ ...prev, currentPayment: "" })); }}
                    >
                      {isSel && <span className="fc2-enroll-card__check"><CheckCircle2 size={18} /></span>}
                      <span className="fc2-enroll-card__name">{ca.courseId?.name || "Course"}</span>
                      <span className="fc2-enroll-card__code">{ca.batchId?.name || ""}</span>
                      <span className={`fc2-badge fc2-badge--${badge.cls}`}>{badge.label}</span>
                      <div className="fc2-enroll-card__fees">
                        <div><strong>{formatCurrency(t)}</strong><span>Total Fees</span></div>
                        <div><strong>{formatCurrency(pd)}</strong><span>Paid</span></div>
                        <div><strong className={p > 0 ? "fc2-amber" : ""}>{formatCurrency(p)}</strong><span>Pending</span></div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Fee Plan & Installments */}
          {selectedStudent && selectedAssignment && !isEditMode && (
            <section className="fc2-card">
              <div className="fc2-card__head">
                <h3 className="fc2-card__title">
                  Fee Plan &amp; Installments
                  {planCount > 0 && <span className="fc2-plan-pill">Plan: {planCount} EMI Plan</span>}
                </h3>
                <button type="button" className="fc2-link-btn" onClick={() => setShowPlanModal(true)}>
                  <FileText size={14} /> {feePlan ? "View Full Plan" : "Set up Plan"}
                </button>
              </div>
              {planLoading ? (
                <div className="fc2-muted"><Spinner size="sm" /> Loading plan…</div>
              ) : !feePlan ? (
                <p className="fc2-muted">No installment plan for this course. Collect the full amount below, or set up an EMI plan.</p>
              ) : (
                <div className="fc2-timeline">
                  {feePlan.installments.map((inst, idx) => {
                    const meta = INSTALLMENT_STATUS[inst.status] || INSTALLMENT_STATUS.upcoming;
                    const bal = Math.max((Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0), 0);
                    const selectable = inst.status !== "paid" && inst.status !== "waived";
                    const isSel = selectedInstallment?.seq === inst.seq;
                    return (
                      <React.Fragment key={inst.seq}>
                        {idx > 0 && <span className="fc2-timeline__link" />}
                        <button
                          type="button"
                          className={`fc2-emi fc2-emi--${meta.cls}${isSel ? " is-selected" : ""}`}
                          onClick={() => handleSelectInstallment(inst)}
                          disabled={!selectable}
                        >
                          <span className={`fc2-emi__num fc2-emi__num--${meta.cls}`}>
                            {inst.status === "paid" ? <CheckCircle2 size={16} /> : inst.seq}
                          </span>
                          <div className="fc2-emi__body">
                            <div className="fc2-emi__top">
                              <span className="fc2-emi__label">{inst.label || `EMI ${inst.seq}`}</span>
                              <span className={`fc2-emi__badge fc2-emi__badge--${meta.cls}`}>{meta.label}</span>
                            </div>
                            <span className="fc2-emi__amount">{formatCurrency(inst.amount)}</span>
                            <span className="fc2-emi__due"><Calendar size={11} /> {fmtDate(inst.dueDate)}</span>
                            {inst.status === "partial" && <span className="fc2-emi__bal">Pending {formatCurrency(bal)}</span>}
                          </div>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Payment Details */}
          <section className="fc2-card">
            <div className="fc2-card__head"><h3 className="fc2-card__title">Payment Details</h3></div>
            {feeLoading ? (
              <div className="fc2-muted"><Spinner size="sm" /> Loading student details…</div>
            ) : (
              <>
                {/* Channel toggle */}
                <div className="fc2-channel">
                  <button
                    type="button"
                    className={`fc2-channel__opt${payChannel === "manual" ? " is-active" : ""}`}
                    onClick={() => setChannel("manual")}
                    disabled={isRazorpayTxn}
                  >
                    <span className="fc2-channel__dot" /> Manual Payment
                  </button>
                  {onlineEnabled && (
                    <button
                      type="button"
                      className={`fc2-channel__opt${payChannel === "online" ? " is-active" : ""}`}
                      onClick={() => setChannel("online")}
                      disabled={isRazorpayTxn}
                    >
                      <span className="fc2-channel__dot" /> Online Payment
                    </button>
                  )}
                </div>

                <div className="fc2-fields">
                  {/* Amount */}
                  <div className="fc2-field">
                    <label>Payment Amount <span className="fc2-req">*</span></label>
                    <div className="fc2-rupee">
                      <IndianRupee size={14} />
                      <input
                        type="number"
                        name="currentPayment"
                        value={form.currentPayment}
                        onChange={handleChange}
                        min={1}
                        max={pendingFee || undefined}
                        placeholder={isSelfPaced ? `Full payment: ₹${pendingFee}` : "Enter amount"}
                        disabled={amountDisabled}
                        readOnly={isSelfPaced || isRazorpayTxn}
                      />
                    </div>
                    {isSelfPaced && !isRazorpayTxn && pendingFee > 0 && (
                      <span className="fc2-hint">Self-paced courses require full payment</span>
                    )}
                  </div>

                  {/* Due after */}
                  <div className="fc2-field">
                    <label>Due After Payment</label>
                    <input type="text" className="fc2-readonly" readOnly value={due < 0 ? formatCurrency(0) : formatCurrency(due)} />
                  </div>

                  {payChannel === "manual" && (
                    <>
                      <div className="fc2-field">
                        <label>Payment Date <span className="fc2-req">*</span></label>
                        <input type="date" name="date" value={form.date} onChange={handleChange} disabled={!selectedStudent} />
                      </div>
                      <div className="fc2-field">
                        <label>Paid To <span className="fc2-req">*</span></label>
                        <input name="paidTo" value={form.paidTo} onChange={handleChange} placeholder="Account / recipient name" disabled={!selectedStudent || isRazorpayTxn} />
                      </div>
                      <div className="fc2-field">
                        <label>Transaction / Reference ID</label>
                        <input
                          name="referenceNumber"
                          value={form.referenceNumber}
                          onChange={handleChange}
                          placeholder="e.g. UPI1234567890"
                          disabled={!selectedStudent || form.paymentMode === "Cash" || isRazorpayTxn}
                        />
                      </div>
                      <div className="fc2-field">
                        <label>Payment Mode <span className="fc2-req">*</span></label>
                        <select
                          name="paymentMode"
                          value={PAYMENT_MODES.includes(form.paymentMode) ? form.paymentMode : ""}
                          onChange={handleChange}
                          disabled={!selectedStudent || isRazorpayTxn}
                        >
                          <option value="">Select mode</option>
                          {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="fc2-field">
                        <label>Received By <span className="fc2-req">*</span></label>
                        <input name="receivedBy" value={form.receivedBy} onChange={handleChange} placeholder="Staff name" disabled={!selectedStudent} />
                      </div>
                      {!feePlan && (due > 0 || form.nextPaymentDate) && (
                        <div className="fc2-field">
                          <label>Next Payment Date</label>
                          <input type="date" name="nextPaymentDate" value={form.nextPaymentDate} onChange={handleChange} disabled={!selectedStudent} />
                        </div>
                      )}
                    </>
                  )}

                  {payChannel === "online" && (
                    <div className="fc2-field fc2-field--full">
                      <label>Online Method <span className="fc2-req">*</span></label>
                      <div className="fc2-online-opts">
                        <button type="button" className={`fc2-online-opt${isOnlineMode ? " is-active" : ""}`} onClick={() => setForm((p) => ({ ...p, paymentMode: "Razorpay (Online)" }))}>
                          <Wifi size={14} /> Pay via Razorpay now
                        </button>
                        <button type="button" className={`fc2-online-opt${isPaymentLinkMode ? " is-active" : ""}`} onClick={() => setForm((p) => ({ ...p, paymentMode: "Send Payment Link to Student" }))}>
                          <Mail size={14} /> Send Payment Link
                        </button>
                      </div>
                    </div>
                  )}

                  {payChannel === "online" && isPaymentLinkMode && (
                    <div className="fc2-field fc2-field--full">
                      <label>Send Link To (Email) <span className="fc2-req">*</span></label>
                      <input type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="student@example.com" disabled={!selectedStudent} />
                    </div>
                  )}
                </div>

                {/* Proof + notes */}
                {payChannel === "manual" ? (
                  <div className="fc2-proof">
                    <div className="fc2-field">
                      <label>Upload Payment Proof</label>
                      {selectedStudent ? (
                        <UploadWithS3
                          s3KeyPrefix={`students/fee-screenshots/${selectedStudent._id}`}
                          accept="image/*"
                          onUpload={(key) => setForm((prev) => ({ ...prev, refImage: key }))}
                          onDelete={() => setForm((prev) => ({ ...prev, refImage: null }))}
                          value={form.refImage}
                        />
                      ) : (
                        <p className="fc2-muted">Select a student to upload proof</p>
                      )}
                    </div>
                    <div className="fc2-field">
                      <label>Notes (Optional)</label>
                      <textarea name="comments" rows={3} value={form.comments} onChange={handleChange} placeholder="Add any notes about this payment..." disabled={!selectedStudent} />
                    </div>
                  </div>
                ) : (
                  <div className="fc2-field">
                    <label>Notes (Optional)</label>
                    <textarea name="comments" rows={2} value={form.comments} onChange={handleChange} placeholder="Add any notes about this payment..." disabled={!selectedStudent} />
                  </div>
                )}

                {isOnlineMode && rzpProcessing && (
                  <div className="fc2-muted"><Spinner size="sm" /> Processing online payment via Razorpay...</div>
                )}

                {lastPaymentInfo && (
                  <div className="fc2-commission">
                    <span>Collected {formatCurrency(lastPaymentInfo.amount)}</span>
                    <span>Commission −{formatCurrency(lastPaymentInfo.commission)}</span>
                    {lastPaymentInfo.gatewayFee > 0 && <span>Gateway −{formatCurrency(lastPaymentInfo.gatewayFee)}</span>}
                    <strong>Net {formatCurrency(lastPaymentInfo.netPayable)}</strong>
                  </div>
                )}

                {err && <div className="fc2-error">{err}</div>}
              </>
            )}
          </section>
        </div>

        {/* ══════════ SIDEBAR ══════════ */}
        <aside className="fc2-side">
          {/* Student Summary */}
          <section className="fc2-card fc2-side-card">
            <h4 className="fc2-side__title">Student Summary</h4>
            {selectedStudent ? (
              <>
                <div className="fc2-side-student">
                  <UserAvatar name={selectedStudent.name} size="md" />
                  <div>
                    <span className="fc2-side-student__name">{selectedStudent.name}</span>
                    <span className="fc2-side-student__meta">{selectedStudent.mobile}{selectedStudent.email ? ` • ${selectedStudent.email}` : ""}</span>
                  </div>
                </div>
                <div className="fc2-side-rows">
                  <div className="fc2-side-row"><span>Course</span><strong>{selectedAssignment?.courseId?.name || selectedStudent.course?.name || "—"}</strong></div>
                  <div className="fc2-side-row"><span>Batch</span><strong>{selectedAssignment?.batchId?.name || selectedStudent.batch?.name || "—"}</strong></div>
                  {selectedAssignment?.joinedDate && (
                    <div className="fc2-side-row"><span>Enrollment Date</span><strong>{fmtDate(selectedAssignment.joinedDate)}</strong></div>
                  )}
                </div>
              </>
            ) : (
              <p className="fc2-muted">Select a student to see their details</p>
            )}
          </section>

          {/* Fee Summary */}
          {selectedStudent && (
            <section className="fc2-card fc2-side-card">
              <h4 className="fc2-side__title">Fee Summary</h4>
              <div className="fc2-side-rows">
                <div className="fc2-side-row"><span>Total Fees</span><strong>{formatCurrency(totalFee)}</strong></div>
                <div className="fc2-side-row"><span>Total Paid</span><strong className="fc2-green">{formatCurrency(paidFee)}</strong></div>
                <div className="fc2-side-row"><span>Total Pending</span><strong className="fc2-amber">{formatCurrency(pendingFee)}</strong></div>
                {feePlan && (
                  <div className="fc2-side-row"><span>Upcoming Dues ({pendingInstallments.length})</span><strong>{formatCurrency(upcomingDues)}</strong></div>
                )}
                {feePlan && (
                  <div className={`fc2-side-row${overdueCount > 0 ? " fc2-side-row--danger" : ""}`}>
                    <span>Overdue ({overdueCount})</span><strong>{formatCurrency(overdueAmount)}</strong>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Current EMI Details */}
          {currentEMI && feePlan && (
            <section className="fc2-card fc2-side-card">
              <h4 className="fc2-side__title">Current EMI Details</h4>
              <div className="fc2-side-rows">
                <div className="fc2-side-row"><span>EMI</span><strong>EMI {currentEMI.seq} of {planCount}</strong></div>
                <div className="fc2-side-row"><span>Due Date</span><strong>{fmtDate(currentEMI.dueDate)}</strong></div>
                <div className="fc2-side-row"><span>EMI Amount</span><strong>{formatCurrency(currentEMI.amount)}</strong></div>
                <div className="fc2-side-row"><span>Paid</span><strong className="fc2-green">{formatCurrency(currentEMI.paidAmount)}</strong></div>
                <div className="fc2-side-row"><span>Pending</span><strong className="fc2-red">{formatCurrency(Math.max((currentEMI.amount || 0) - (currentEMI.paidAmount || 0), 0))}</strong></div>
              </div>
            </section>
          )}

          {/* Quick Actions */}
          {selectedStudent && selectedAssignment && (
            <section className="fc2-card fc2-side-card">
              <h4 className="fc2-side__title">Quick Actions</h4>
              <div className="fc2-quick">
                {onlineEnabled && (
                  <button type="button" className="fc2-quick__btn" onClick={handleQuickLink}>
                    <Link2 size={18} /><span>Generate Payment Link</span>
                  </button>
                )}
                <button type="button" className="fc2-quick__btn" onClick={handleSendWhatsApp} disabled={!feePlan || !nextDueInstallment || sendingWa}>
                  <MessageSquare size={18} /><span>{sendingWa ? "Sending…" : "Send via WhatsApp"}</span>
                </button>
                <button type="button" className="fc2-quick__btn" onClick={() => setShowPlanModal(true)}>
                  <FileText size={18} /><span>{feePlan ? "View Fee Plan" : "Set up Fee Plan"}</span>
                </button>
              </div>
            </section>
          )}
        </aside>
      </div>

      {/* Fixed bottom action bar */}
      <FormActionBar>
        <AppButton variant="outline" type="button" onClick={onCancel}>Cancel</AppButton>
        <ResetButton onReset={handleReset} disabled={!selectedStudent || editLoading} />
        <AppButton
          onClick={isEditMode ? handleSubmit : handlePreview}
          disabled={!selectedStudent || disabledSave || rzpProcessing || rzpLoading || linkSubmitting || editLoading}
        >
          {(rzpProcessing || rzpLoading || linkSubmitting) ? (
            <><Spinner size="sm" className="me-1" /> Processing...</>
          ) : isEditMode ? (
            <><CreditCard size={16} className="me-1" /> Save Changes</>
          ) : isPaymentLinkMode ? (
            <><Mail size={16} className="me-1" /> Send Payment Link</>
          ) : isOnlineMode ? (
            <><Wifi size={16} className="me-1" /> Pay via Razorpay</>
          ) : (
            <><CreditCard size={16} className="me-1" /> Collect Fee</>
          )}
        </AppButton>
      </FormActionBar>

      {/* Preview & Confirm modal (add mode only) */}
      <AppModal
        show={showPreview}
        onHide={() => { if (!disabledSave && !linkSubmitting) setShowPreview(false); }}
        title="Preview & Confirm"
        icon={<Receipt size={18} />}
        size="md"
        footer={
          <>
            <AppButton variant="outline" type="button" onClick={() => setShowPreview(false)} disabled={disabledSave || linkSubmitting}>
              Back
            </AppButton>
            <AppButton onClick={handleSubmit} disabled={disabledSave || linkSubmitting || rzpProcessing}>
              {(disabledSave || linkSubmitting) ? (
                <><Spinner size="sm" className="me-1" /> {isPaymentLinkMode ? "Sending..." : "Processing..."}</>
              ) : isPaymentLinkMode ? (
                <><Mail size={16} className="me-1" /> Confirm &amp; Send Link</>
              ) : isOnlineMode ? (
                <><Wifi size={16} className="me-1" /> Confirm &amp; Pay</>
              ) : (
                <><CreditCard size={16} className="me-1" /> Confirm &amp; Collect</>
              )}
            </AppButton>
          </>
        }
      >
        <div className="fee-preview">
          {/* Student */}
          <div className="fee-preview__student">
            <UserAvatar name={selectedStudent?.name || "?"} size="md" />
            <div className="fee-preview__student-info">
              <span className="fee-preview__name">{selectedStudent?.name}</span>
              <span className="fee-preview__meta">
                {selectedStudent?.mobile}{selectedStudent?.email ? ` • ${selectedStudent.email}` : ""}
              </span>
            </div>
          </div>

          {/* Amount */}
          <div className="fee-preview__amount">
            <span className="fee-preview__amount-label">Payment Amount</span>
            <span className="fee-preview__amount-value">{formatCurrency(form.currentPayment)}</span>
          </div>

          {/* Details */}
          <div className="fee-preview__rows">
            <div className="fee-preview__row">
              <span>Course</span>
              <strong>{selectedAssignment?.courseId?.name || selectedStudent?.course?.name || selectedStudent?.course || "—"}</strong>
            </div>
            {(selectedAssignment?.batchId?.name || selectedStudent?.batch?.name || selectedStudent?.batch) && (
              <div className="fee-preview__row">
                <span>Batch</span>
                <strong>{selectedAssignment?.batchId?.name || selectedStudent?.batch?.name || selectedStudent?.batch}</strong>
              </div>
            )}
            <div className="fee-preview__row">
              <span>Payment Mode</span>
              <strong>{isPaymentLinkMode ? "Online Payment Link" : (form.paymentMode || "—")}</strong>
            </div>
            {isPaymentLinkMode && (
              <div className="fee-preview__row">
                <span>Send To</span>
                <strong>{linkEmail}</strong>
              </div>
            )}
            {!isPaymentLinkMode && (
              <div className="fee-preview__row">
                <span>Date</span>
                <strong>
                  {form.date ? new Date(form.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—"}
                </strong>
              </div>
            )}
            {form.referenceNumber && (
              <div className="fee-preview__row">
                <span>Transaction ID</span>
                <strong>{form.referenceNumber}</strong>
              </div>
            )}
            {!isOnlineMode && form.paidTo && (
              <div className="fee-preview__row">
                <span>Paid To</span>
                <strong>{form.paidTo}</strong>
              </div>
            )}
            {form.comments && (
              <div className="fee-preview__row fee-preview__row--notes">
                <span>Notes</span>
                <strong>{form.comments}</strong>
              </div>
            )}
            <div className="fee-preview__row fee-preview__row--due">
              <span>Due After Payment</span>
              <strong>{due <= 0 ? formatCurrency(0) : formatCurrency(due)}</strong>
            </div>
            {due > 0 && form.nextPaymentDate && (
              <div className="fee-preview__row">
                <span>Next Payment Date</span>
                <strong>
                  {new Date(form.nextPaymentDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}
                </strong>
              </div>
            )}
          </div>

          {isOnlineMode && (
            <p className="fee-preview__hint">
              <Info size={13} /> You'll be redirected to Razorpay to complete this payment securely.
            </p>
          )}
          {isPaymentLinkMode && (
            <p className="fee-preview__hint">
              <Info size={13} /> A secure payment link will be emailed to the student. It stays valid for 7 days, and the balance is re-checked when they pay.
            </p>
          )}
        </div>
      </AppModal>

      {/* Payment link sent — success modal */}
      <AppModal
        show={!!sentLink}
        onHide={() => setSentLink(null)}
        title="Payment Link Sent"
        icon={<CheckCircle2 size={18} />}
        size="md"
        footer={
          <>
            <AppButton variant="outline" type="button" onClick={() => setSentLink(null)}>
              Close
            </AppButton>
            <AppButton
              type="button"
              onClick={() => { setSentLink(null); setForm(initialData); setSelectedStudent(null); setLinkEmail(""); }}
            >
              Done
            </AppButton>
          </>
        }
      >
        {sentLink && (
          <div className="fee-link-success">
            <p className="fee-link-success__msg">
              A secure payment link has been emailed to <strong>{sentLink.email}</strong>. You can also copy and share it directly.
            </p>
            <div className="fee-link-success__url">
              <input type="text" readOnly value={sentLink.url} onFocus={(e) => e.target.select()} />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(sentLink.url);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch { /* clipboard unavailable */ }
                }}
              >
                {linkCopied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <a className="fee-link-success__open" href={sentLink.url} target="_blank" rel="noreferrer">
              <Link2 size={14} /> Open link
            </a>
          </div>
        )}
      </AppModal>

      {/* Installment plan editor */}
      <FeePlanModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        courseAssignment={selectedAssignment}
        existingPlan={feePlan}
        onSaved={() => { setShowPlanModal(false); loadPlan(selectedAssignment?._id); }}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default FeeCollectionForm;
